import { Span } from "@vp/swan";
import React from "react";
import { useLocalization } from "../../hooks/useLocalizations";
import { ComparativeDisplayType, IPlusOrMinus, ZeroDisplayType } from "../../types/pricing";

interface PlusOrMinusProps {
  pricingInfo: IPlusOrMinus;
  /** @deprecated ignored; synchronous rendering */
  placeHolderText?: string;
  /** @deprecated use className instead */
  classNameWrapper?: string;
  className?: string;
  /** @deprecated not used */
  tenant?: string;
}

function formatPriceDifference(
  locale: string,
  currency: string,
  price: number,
  minimumFractionDigits: number,
): string {
  const formatted = new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
    minimumFractionDigits,
  }).format(Math.abs(price));
  if (price > 0) return `+${formatted}`;
  if (price < 0) return `-${formatted}`;
  return formatted;
}

function shouldShowComparativePlusOrMinus(
  priceDifference: number,
  priceDifferenceDiscounted: number,
  comparativeDisplayType: ComparativeDisplayType,
): boolean {
  if (comparativeDisplayType === ComparativeDisplayType.Always) return true;
  if (comparativeDisplayType === ComparativeDisplayType.Never) return false;
  // CustomerFriendly/Legacy: show when discounted is less than list
  return (
    priceDifference !== priceDifferenceDiscounted &&
    Math.abs(priceDifferenceDiscounted) < Math.abs(priceDifference)
  );
}

const PlusOrMinus: React.FC<PlusOrMinusProps> = ({
  pricingInfo,
  classNameWrapper,
  className,
}) => {
  const { t } = useLocalization();
  const wrapperClass = classNameWrapper || className;
  const { priceDifference, priceDifferenceDiscounted, currency, culture, minimumFractionDigits } = pricingInfo;
  const comparativeDisplayType = pricingInfo.comparativeDisplayType ?? ComparativeDisplayType.CustomerFriendly;
  const zeroDisplayType = pricingInfo.zeroDisplayType ?? ZeroDisplayType.Legacy;

  let listDisplay: string;
  if (priceDifference === 0) {
    switch (zeroDisplayType) {
      case ZeroDisplayType.Zero:
        listDisplay = formatPriceDifference(culture, currency, 0, minimumFractionDigits);
        break;
      case ZeroDisplayType.BasePrice:
        listDisplay = t("price-platform.token-price.base-price-label") as string;
        break;
      case ZeroDisplayType.Included:
        listDisplay = t("price-platform.token-price.included-label") as string;
        break;
      default:
        // Legacy: use base price label as fallback
        listDisplay = t("price-platform.token-price.base-price-label") as string;
    }
  } else {
    listDisplay = formatPriceDifference(culture, currency, priceDifference, minimumFractionDigits);
  }

  const showComparison = shouldShowComparativePlusOrMinus(
    priceDifference,
    priceDifferenceDiscounted,
    comparativeDisplayType,
  );

  if (showComparison) {
    const discountDisplay = formatPriceDifference(culture, currency, priceDifferenceDiscounted, minimumFractionDigits);
    return (
      <Span className={["swan-pricing", wrapperClass].filter(Boolean).join(" ")}>
        <Span className="swan-pricing-comparison">
          <Span className="swan-list-price">{listDisplay}</Span>{" "}
          <Span className="swan-discount-price">{discountDisplay}</Span>
        </Span>
      </Span>
    );
  }

  // No comparison — show the discount price if different from list, else show list
  const displayPrice =
    priceDifference !== priceDifferenceDiscounted
      ? formatPriceDifference(culture, currency, priceDifferenceDiscounted, minimumFractionDigits)
      : listDisplay;

  return (
    <Span className={["swan-pricing", wrapperClass].filter(Boolean).join(" ")}>
      <Span className="swan-list-price">{displayPrice}</Span>
    </Span>
  );
};

export default PlusOrMinus;
